import React from 'react';
import classnames from 'classnames';
import { Button, Dropdown, Switch, Tooltip } from 'antd';
import { DeleteOutlined, DownloadOutlined, EditOutlined, HolderOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CopyLinkButton from './CopyLinkButton';
import RenameTableForm from './RenameTableForm';
import { TableListLayout, displayLink } from './tableList.utils';

/**
 * One table: drag handle (also ↑ / ↓ when focused), name with its link under it, on/off, Copy link,
 * Download QR code, rename and delete. One line on a wide card, two lines on a narrow one, and on a
 * phone the name first with Rename and Delete in a ⋮ menu.
 */
export default function TableRow({
  table,
  tables,
  layout,
  dragHandleProps = {},
  reorderDisabled = false,
  renaming = false,
  onStartRename,
  onStopRename,
  onRename,
  onToggle,
  onDelete,
  onDownloadQr,
  onMove,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Tables:TableList' });
  const phone = layout === TableListLayout.Phone;

  const handle = (
    <Tooltip title={reorderDisabled ? t('tooltips.move_disabled') : t('tooltips.move')}>
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        className={classnames({ 'cursor-grab': !reorderDisabled })}
        disabled={reorderDisabled}
        data-table-handle={table.id}
        {...(reorderDisabled ? {} : dragHandleProps)}
        aria-label={t('aria.move', { name: table.name })}
        onKeyDown={(event) => {
          if (reorderDisabled || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) {
            return;
          }

          event.preventDefault();
          onMove(table, event.key === 'ArrowUp' ? -1 : 1);
        }}
      />
    </Tooltip>
  );

  const name = (
    <div className="min-w-0">
      {renaming ? (
        <RenameTableForm table={table} tables={tables} onRename={onRename} onDone={onStopRename} />
      ) : (
        <span
          className={classnames('block', phone ? 'break-words' : 'truncate', {
            'font-semibold': table.is_active,
            'text-gray-400': !table.is_active,
          })}
          title={table.name}
          data-testid="table-name"
        >
          {table.name}
        </span>
      )}
      <span className="mt-nano block truncate font-mono text-xs text-gray-500" title={table.link ?? undefined}>
        {table.link ? displayLink(table.link) : t('no_link')}
      </span>
    </div>
  );

  const onOff = (
    <label className="inline-flex items-center gap-xs whitespace-nowrap text-sm cursor-pointer">
      <Switch
        checked={table.is_active}
        onChange={(checked) => onToggle(table, checked)}
        aria-label={t('aria.switch', { name: table.name })}
        data-table-switch={table.id}
      />
      <span className="min-w-[24px]">{table.is_active ? t('switch.on') : t('switch.off')}</span>
    </label>
  );

  const copy = <CopyLinkButton link={table.link} name={table.name} ghost={phone} />;

  const qr = (
    <Button
      size="small"
      type={phone ? 'text' : 'default'}
      icon={<DownloadOutlined />}
      disabled={!table.link}
      onClick={() => onDownloadQr(table)}
      aria-label={t('aria.download_qr', { name: table.name })}
      data-testid="table-download-qr"
    >
      {phone ? t('buttons.qr_short') : t('buttons.download_qr')}
    </Button>
  );

  const actions = (
    <span className="inline-flex gap-2xs">
      <Tooltip title={t('buttons.rename')}>
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          onClick={() => onStartRename(table)}
          aria-label={t('aria.rename', { name: table.name })}
          data-table-rename={table.id}
        />
      </Tooltip>
      <Tooltip title={t('buttons.delete')}>
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onDelete(table)}
          aria-label={t('aria.delete', { name: table.name })}
        />
      </Tooltip>
    </span>
  );

  const more = (
    <Dropdown
      trigger={['click']}
      menu={{
        items: [
          { key: 'rename', icon: <EditOutlined />, label: t('buttons.rename') },
          { key: 'delete', icon: <DeleteOutlined />, label: t('buttons.delete'), danger: true },
        ],
        onClick: ({ key }) => (key === 'rename' ? onStartRename(table) : onDelete(table)),
      }}
    >
      <Button
        type="text"
        size="small"
        icon={<MoreOutlined />}
        aria-label={t('aria.more', { name: table.name })}
        data-table-rename={table.id}
      />
    </Dropdown>
  );

  const rowClass = 'border-b border-gray-200 bg-white last:border-b-0';

  // A row of the wide card's table: SortableItem renders the <tr>.
  if (layout === TableListLayout.Wide) {
    const cell = 'border-b border-gray-200 px-xs py-xs align-middle';

    return (
      <>
        <td className={classnames(cell, 'w-[40px] pr-0')}>{handle}</td>
        <td className={classnames(cell, 'max-w-0 w-full')}>{name}</td>
        <td className={classnames(cell, 'whitespace-nowrap')}>{onOff}</td>
        <td className={classnames(cell, 'whitespace-nowrap')}>{copy}</td>
        <td className={classnames(cell, 'whitespace-nowrap')}>{qr}</td>
        <td className={classnames(cell, 'whitespace-nowrap pl-0')}>{actions}</td>
      </>
    );
  }

  if (layout === TableListLayout.Narrow) {
    return (
      <div className={classnames(rowClass, 'px-xs py-sm')} data-testid="table-row">
        <div className="flex items-center gap-xs">
          {handle}
          <div className="min-w-0 flex-1">{name}</div>
          {onOff}
        </div>
        <div className="mt-xs flex flex-wrap items-center gap-xs pl-[40px]">
          {copy}
          {qr}
          <div className="ml-auto">{actions}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={classnames(rowClass, 'px-xs py-sm')} data-testid="table-row">
      <div className="flex items-start gap-xs">
        {handle}
        <div className="min-w-0 flex-1">{name}</div>
        {!renaming && (
          <>
            <div className="pt-nano">{onOff}</div>
            {more}
          </>
        )}
      </div>
      <div className="mt-2xs flex flex-wrap gap-2xs pl-[40px]">
        {copy}
        {qr}
      </div>
    </div>
  );
}
